import { generateClient } from 'aws-amplify/api';

/**
 * Shared pagination helper for AppSync/DynamoDB list queries.
 *
 * Every generated list query (`list*`, `*ByTenant`, `*ByList`, ...) returns at
 * most one page of results plus a `nextToken`. The page size is capped by the
 * resolver (and by DynamoDB's 1MB read limit), so a single call is NOT a
 * complete result set even when no `limit` is supplied. Passing a large `limit`
 * is also not a fix: for filtered queries DynamoDB applies `limit` BEFORE the
 * filter, so a page can come back short (or empty) while more matches remain
 * behind a `nextToken`.
 *
 * Use this any time the UI needs the full set (tables, dropdowns, integrity
 * checks). Do not use it for intentionally capped reads such as "latest 50
 * notifications" or a bounded preview.
 *
 * @param {object} params
 * @param {string} params.query      Generated GraphQL query document.
 * @param {string} params.resultKey  Field name on `data` holding the connection,
 *                                   e.g. 'profileByTenant'.
 * @param {object} [params.variables] Base variables, reused on every page.
 *                                   `nextToken` is managed by this helper.
 * @param {object} [params.client]   Optional pre-built client.
 * @returns {Promise<Array>} Every item across all pages.
 */
export async function fetchAllPages({
	query,
	resultKey,
	variables = {},
	client,
}) {
	const gqlClient = client ?? generateClient();
	const allItems = [];
	let nextToken;

	do {
		const pageVariables = { ...variables };
		if (nextToken) {
			pageVariables.nextToken = nextToken;
		}

		const response = await gqlClient.graphql({
			query,
			variables: pageVariables,
		});

		const result = response?.data?.[resultKey];
		if (result) {
			allItems.push(...(result.items ?? []));
			// Coerce undefined to null so the loop always terminates even if the
			// resolver omits nextToken.
			nextToken = result.nextToken ?? null;
		} else {
			nextToken = null;
		}
	} while (nextToken);

	return allItems;
}
